/**
 * VÉRAULT Portal — Client · Messages (Wave 30.2 · Phase 8a)
 * ───────────────────────────────────────────────────────────────
 * Editorial concierge experience.
 *
 * Replaces the Slack-style two-pane chat with a calm, full-bleed
 * editorial surface. Mobile-first: thread list collapses into a
 * detail view (Apple-mail-style stack), not a shrinking sidebar.
 *
 * Visual language:
 *   - Editorial "letter" messages — full-width paragraphs with calm
 *     attribution above. NO rounded chat bubbles, NO Slack blobs.
 *   - Serif thread titles. Mono uppercase eyebrows. Day dividers as
 *     hairline rules with a quiet date label.
 *   - Composer reads like writing a quiet note on cream paper.
 *   - Typing indicator: calm gold pulse + "Your advisor is composing…"
 *
 * Live sync: subscribes to `vt:sync-changed:messages` from useLiveSync
 * (Wave 7c) — no separate polling here. Foreground fetch on visibility
 * change so a returning traveler sees fresh threads instantly.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Paperclip, Loader2, MessageSquare, ChevronLeft,
  Plane, X, Sparkles, Mail, MailOpen,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Surface, PageHeader, Empty, Skeleton,
  PortalButton, api, motion as portalMotion,
} from "../../index";
import { AttachmentPicker } from "../../components/AttachmentPicker";
import { AttachmentGrid, relTime, dayLabel } from "../../components/conciergeShared";
import {
  useTypingHeartbeat,
  useChatNotifications,
  primeAudio,
} from "../../components/conciergeRealtime";

const { fadeInUp, stagger } = portalMotion;

function fireChanged() {
  window.dispatchEvent(new CustomEvent("verault:concierge-changed"));
}

// ─── iMessage-style message bubble ────────────────────────────────────
// Wave 12.g — replaced the editorial "letter" layout with a chat bubble
// composition. Client messages right-align in gold; advisor messages
// left-align in stone. Hover/long-press reveals edit + delete actions
// for the recipient's OWN messages. Timestamps fade in on the row.
function MessageLetter({ msg, prev, convId, onChanged }) {
  const mine = msg.sender_role === "client";
  const continuation = prev && prev.sender_role === msg.sender_role;
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(msg.body || "");
  const [busy, setBusy] = React.useState(false);

  const cancelEdit = () => { setDraft(msg.body || ""); setEditing(false); };

  const saveEdit = async () => {
    const next = draft.trim();
    if (!next || next === msg.body) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/client-portal/concierge/conversations/${convId}/messages/${msg.id}`, { body: next });
      onChanged?.();
      setEditing(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't edit message");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/client-portal/concierge/conversations/${convId}/messages/${msg.id}`);
      onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't delete message");
    } finally {
      setBusy(false);
    }
  };

  // Feb-2026 · Per-message "Mark unread from here" for the client.
  // Isolated from advisor state on the server — this only affects the
  // client's read state, never the advisor's.
  const markUnreadFromHere = async () => {
    setBusy(true);
    try {
      await api.post(`/client-portal/concierge/conversations/${convId}/messages/${msg.id}/mark-unread`);
      onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't mark unread");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`group flex ${mine ? "justify-end" : "justify-start"} ${continuation ? "mt-1" : "mt-3"}`}
      data-testid={`message-${msg.id}`}
    >
      <div className={`flex items-end gap-2 max-w-[78%] ${mine ? "flex-row-reverse" : ""}`}>
        <div className="flex flex-col items-stretch gap-1">
          {!continuation && (
            <span className={`font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] ${mine ? "text-right" : ""}`}>
              {mine ? "You" : msg.sender_name || "Your advisor"}
            </span>
          )}
          {editing ? (
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(6, Math.max(1, draft.split("\n").length))}
                className="px-3.5 py-2.5 rounded-[18px] bg-white border border-[hsl(var(--accent-gold))]/60 outline-none text-[15px] leading-[1.45] font-serif min-w-[200px] focus:border-[hsl(var(--accent-gold))]"
                autoFocus
                data-testid={`message-${msg.id}-edit-input`}
              />
              <div className="flex flex-col gap-1 mb-1">
                <button onClick={saveEdit} disabled={busy} className="text-[10px] font-mono uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))] hover:opacity-80" data-testid={`message-${msg.id}-save`}>Save</button>
                <button onClick={cancelEdit} disabled={busy} className="text-[10px] font-mono uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] hover:opacity-80" data-testid={`message-${msg.id}-cancel`}>Cancel</button>
              </div>
            </div>
          ) : (
            <div
              className={`relative px-3.5 py-2 rounded-[18px] shadow-sm ${
                mine
                  ? "bg-[hsl(var(--accent-gold))] text-white"
                  : "bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground))]"
              } ${continuation ? (mine ? "rounded-tr-md" : "rounded-tl-md") : ""}`}
              data-testid={`message-${msg.id}-bubble`}
            >
              <p className="text-[15px] leading-[1.45] whitespace-pre-wrap font-serif">
                {msg.body}
                {msg.edited_at && (
                  <span className={`ml-1.5 font-mono text-[9px] uppercase tracking-[0.12em] ${mine ? "text-white/70" : "text-[hsl(var(--foreground-muted))]"}`}>
                    · edited
                  </span>
                )}
              </p>
              {msg.attachments && msg.attachments.length > 0 ? (
                <div className="mt-2 -mx-1">
                  <AttachmentGrid attachments={msg.attachments} />
                </div>
              ) : null}
            </div>
          )}
          <span className={`font-mono text-[9.5px] text-[hsl(var(--foreground-muted))] opacity-0 group-hover:opacity-100 transition-opacity ${mine ? "text-right" : ""}`}>
            {relTime(msg.created_at)}
          </span>
        </div>

        {/* Edit / delete affordances for the client's OWN messages */}
        {mine && !editing && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 mb-6">
            <button
              onClick={markUnreadFromHere}
              disabled={busy}
              title="Mark unread from here (just for me)"
              className="text-[10px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              data-testid={`message-${msg.id}-mark-unread`}
              aria-label="Mark unread from here"
            >
              ✉
            </button>
            <button
              onClick={() => setEditing(true)}
              disabled={busy}
              title="Edit"
              className="text-[10px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
              data-testid={`message-${msg.id}-edit`}
            >
              ✎
            </button>
            <button
              onClick={remove}
              disabled={busy}
              title="Delete"
              className="text-[10px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))]"
              data-testid={`message-${msg.id}-delete`}
            >
              ✕
            </button>
          </div>
        )}

        {/* Mark-unread affordance on advisor messages (client's read state only) */}
        {!mine && !editing && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 mb-6">
            <button
              onClick={markUnreadFromHere}
              disabled={busy}
              title="Mark unread from here (just for me)"
              className="text-[10px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              data-testid={`message-${msg.id}-mark-unread`}
              aria-label="Mark unread from here"
            >
              ✉
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DayRule({ label }) {
  return (
    <div className="flex items-center gap-4 my-6" aria-hidden>
      <div className="flex-1 h-px bg-[hsl(var(--border))]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">{label}</span>
      <div className="flex-1 h-px bg-[hsl(var(--border))]" />
    </div>
  );
}

// ─── Calm typing indicator ────────────────────────────────────────────
function TypingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex items-center gap-2.5 py-2"
      data-testid="typing-indicator"
    >
      <motion.span
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        className="size-1.5 rounded-full bg-[hsl(var(--accent-gold))]"
      />
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
        Your advisor is composing…
      </span>
    </motion.div>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────
function Composer({ onSend, busy, onTyping }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const taRef = useRef(null);

  const handleSend = async () => {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    await onSend({ body, attachments });
    setText("");
    setAttachments([]);
    taRef.current?.focus();
  };

  return (
    <div
      className="border-t border-[hsl(var(--border))] px-5 sm:px-8 py-4 bg-[hsl(var(--background-paper))]/40"
      data-testid="composer"
    >
      {attachments.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {attachments.map((a, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] text-[12px]"
            >
              <span className="truncate max-w-[180px] text-[hsl(var(--foreground-secondary))]">
                {a.name || a.url}
              </span>
              <button
                onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}
                className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))]"
                type="button"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (e.target.value) onTyping?.();
        }}
        onFocus={() => primeAudio()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSend();
          }
        }}
        rows={3}
        placeholder="Write a quiet note to your advisor…"
        className="w-full resize-none bg-transparent text-[15px] outline-none placeholder:text-[hsl(var(--foreground-muted))] font-serif leading-relaxed"
        data-testid="composer-textarea"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <button
          onClick={() => setPickerOpen(true)}
          className="size-9 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background))] transition-colors"
          title="Attach"
          type="button"
          data-testid="composer-attach"
        >
          <Paperclip className="size-4" strokeWidth={1.5} />
        </button>
        <PortalButton
          variant="primary"
          size="sm"
          onClick={handleSend}
          disabled={busy || (!text.trim() && attachments.length === 0)}
          icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
          data-testid="composer-send"
        >
          Send
        </PortalButton>
      </div>
      <AttachmentPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(att) => setAttachments((arr) => [...arr, att])}
        isClient
      />
    </div>
  );
}

// ─── Thread list (mobile + desktop sidebar) ───────────────────────────
function ThreadList({ conversations, activeConvId, onPick }) {
  if (conversations === null) {
    return (
      <div className="p-3 space-y-2">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }
  if (conversations.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <Sparkles className="size-5 mx-auto mb-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.25} />
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          A quiet inbox
        </p>
        <p className="mt-2 font-serif italic text-[13px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
          When your advisor opens a thread, it will appear here. Peaceful, isn't it?
        </p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[hsl(var(--border))]">
      {conversations.map((c) => (
        <li key={c.id}>
          <button
            onClick={() => onPick(c.id)}
            className={`w-full text-left px-5 py-4 hover:bg-[hsl(var(--background-paper))]/60 transition-colors ${
              activeConvId === c.id ? "bg-[hsl(var(--background-paper))]" : ""
            }`}
            data-testid={`concierge-thread-${c.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {c.trip_id ? (
                    <Plane className="size-3 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} />
                  ) : null}
                  <span className="font-serif text-[14.5px] text-[hsl(var(--foreground))] truncate">
                    {c.trip_name || c.subject || "General"}
                  </span>
                  {c.unread_for_client > 0 && (
                    <span
                      className="size-1.5 rounded-full bg-[hsl(var(--accent-gold))] shrink-0"
                      aria-label="unread"
                      data-testid={`unread-dot-${c.id}`}
                    />
                  )}
                </div>
                <p className="mt-1 text-[12.5px] text-[hsl(var(--foreground-secondary))] truncate font-serif">
                  {c.last_message_preview || (
                    <span className="italic opacity-60">No messages yet</span>
                  )}
                </p>
              </div>
              <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))] shrink-0 pt-1">
                {relTime(c.last_message_at)}
              </span>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function ClientMessages() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialThreadId = searchParams.get("thread");
  const [conversations, setConversations] = useState(null);
  const [activeConvId, setActiveConvId] = useState(initialThreadId || null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sendingBusy, setSendingBusy] = useState(false);
  // Mobile: when a thread is picked, we slide the detail view in.
  const [mobileDetail, setMobileDetail] = useState(!!initialThreadId);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/client-portal/concierge/conversations");
      setConversations(data || []);
    } catch {
      setConversations([]);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    // Wave 30 · Phase 8e — subscribe to centralized live-sync events.
    // Replaces the 30s self-poll. ExperienceLayout-level useLiveSync
    // drives sync-state polling; we react to `messages` domain changes.
    const onMessagesSync = () => fetchConversations();
    window.addEventListener("vt:sync-changed:messages", onMessagesSync);
    // Light catch-up cadence so when Messages is open on its own (no
    // tripId scope) we still feel alive.
    const t = setInterval(fetchConversations, 60000);
    return () => {
      window.removeEventListener("vt:sync-changed:messages", onMessagesSync);
      clearInterval(t);
    };
  }, [fetchConversations]);

  useEffect(() => {
    if (conversations && conversations.length > 0 && !activeConvId) {
      // Auto-select first thread on DESKTOP, leave empty on MOBILE.
      const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
      if (isDesktop) setActiveConvId(conversations[0].id);
    }
  }, [conversations, activeConvId]);

  const fetchActive = useCallback(async () => {
    if (!activeConvId) {
      setActiveConv(null);
      setMessages([]);
      return;
    }
    try {
      const [convRes, msgRes] = await Promise.all([
        api.get(`/client-portal/concierge/conversations/${activeConvId}`),
        api.get(`/client-portal/concierge/conversations/${activeConvId}/messages`),
      ]);
      setActiveConv(convRes.data);
      setMessages(msgRes.data || []);
      if ((convRes.data.unread_for_client || 0) > 0 && !convRes.data.client_unread_since_message_id) {
        // Auto mark-read on open — SKIPPED when the client has a manual
        // unread pointer set (they explicitly want it to stay unread).
        api.post(`/client-portal/concierge/conversations/${activeConvId}/read-all`)
          .then(() => {
            fireChanged();
            fetchConversations();
          })
          .catch(() => {});
      }
    } catch {
      setActiveConv(null);
      setMessages([]);
    }
  }, [activeConvId, fetchConversations]);

  useEffect(() => {
    fetchActive();
    if (!activeConvId) return;
    const t = setInterval(fetchActive, 4000);
    return () => clearInterval(t);
  }, [fetchActive, activeConvId]);

  // Wave 11k — typing heartbeat for outgoing composer
  const sendTypingPing = useTypingHeartbeat({
    role: "client",
    conversationId: activeConvId,
    disabled: !activeConvId,
  });

  // Wave 11k — new-message notification orchestration (chime + browser
  // notification + title flash). Suppresses self-notify. Only fires when
  // the tab is in the background.
  useChatNotifications({
    messages,
    myRole: "client",
    senderLabel: activeConv?.trip_name
      ? `Verault · ${activeConv.trip_name}`
      : "Verault Concierge",
    conversationId: activeConvId,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, activeConvId, activeConv?.advisor_typing]);

  const handleSend = async (payload) => {
    if (!activeConvId) return;
    setSendingBusy(true);
    try {
      await api.post(
        `/client-portal/concierge/conversations/${activeConvId}/messages`,
        payload,
      );
      await fetchActive();
      fetchConversations();
    } finally {
      setSendingBusy(false);
    }
  };

  const handlePickThread = (id) => {
    setActiveConvId(id);
    setMobileDetail(true);
    // Update URL for shareable deep-link.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("thread", id);
      return next;
    }, { replace: true });
  };

  const handleBackToList = () => {
    setMobileDetail(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("thread");
      return next;
    }, { replace: true });
  };

  const grouped = useMemo(() => {
    const groups = [];
    let currentDay = "";
    let prev = null;
    for (const m of messages) {
      const d = dayLabel(m.created_at);
      if (d !== currentDay) {
        groups.push({ kind: "day", label: d, key: `day-${d}-${m.id}` });
        currentDay = d;
        prev = null; // day boundary breaks continuation
      }
      groups.push({ kind: "msg", msg: m, prev, key: m.id });
      prev = m;
    }
    return groups;
  }, [messages]);

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-8"
      data-testid="client-messages-page"
    >
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Messages"
          title="A private line to your advisor."
          lede="Quiet thoughts, questions, requests — composed unhurried, answered with care."
        />
      </motion.div>

      <motion.div
        variants={fadeInUp}
        className="grid grid-cols-12 gap-4 lg:gap-6"
        style={{ minHeight: "calc(100vh - 320px)" }}
      >
        {/* Thread list — visible on desktop always, hidden on mobile when detail open */}
        <div
          className={`col-span-12 md:col-span-4 lg:col-span-3 ${
            mobileDetail ? "hidden md:block" : "block"
          }`}
        >
          <Surface variant="default" padded={false} className="flex flex-col h-full overflow-hidden">
            <div className="px-5 py-4 border-b border-[hsl(var(--border))]">
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
                Threads
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ThreadList
                conversations={conversations}
                activeConvId={activeConvId}
                onPick={handlePickThread}
              />
            </div>
          </Surface>
        </div>

        {/* Thread detail — hidden on mobile until a thread is picked */}
        <div
          className={`col-span-12 md:col-span-8 lg:col-span-9 ${
            !mobileDetail && conversations && conversations.length > 0 ? "hidden md:block" : ""
          }`}
        >
          <AnimatePresence mode="wait">
            {!activeConv ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Surface
                  variant="default"
                  padded
                  className="!p-12 h-full flex items-center justify-center"
                  style={{ minHeight: "60vh" }}
                >
                  <Empty
                    title="A quiet space"
                    hint="When your advisor opens a thread with you, it will appear here."
                    icon={<MessageSquare className="size-6" strokeWidth={1.25} />}
                  />
                </Surface>
              </motion.div>
            ) : (
              <motion.div
                key={activeConvId}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
              >
                <Surface
                  variant="default"
                  padded={false}
                  className="flex flex-col h-full overflow-hidden"
                >
                  {/* Editorial header */}
                  <div className="px-5 sm:px-8 py-5 border-b border-[hsl(var(--border))] bg-[hsl(var(--background-paper))]/50">
                    <div className="flex items-start gap-3">
                      {/* Mobile back button */}
                      <button
                        type="button"
                        onClick={handleBackToList}
                        className="md:hidden size-8 -ml-1 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background))]"
                        aria-label="Back to threads"
                        data-testid="thread-back"
                      >
                        <ChevronLeft className="size-4" strokeWidth={1.5} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                          {activeConv.trip_id ? "Experience thread" : "Concierge thread"}
                        </span>
                        <h2
                          className="mt-1.5 font-serif text-[20px] sm:text-[22px] text-[hsl(var(--foreground))] truncate"
                          data-testid="thread-subject"
                        >
                          {activeConv.trip_name || activeConv.subject || "General"}
                        </h2>
                        {activeConv.trip_id ? (
                          <Link
                            to={`/portal/client/experiences/${activeConv.trip_id}`}
                            className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
                            data-testid="thread-trip-link"
                          >
                            <Plane className="size-3" strokeWidth={1.5} />
                            Open this experience
                          </Link>
                        ) : null}
                      </div>
                      {/* Feb-2026 · Thread-level unread toggle. Isolated
                          per-participant — this only affects the
                          client's own read state. */}
                      <button
                        type="button"
                        onClick={async () => {
                          const isUnread = !!activeConv.client_unread_since_message_id;
                          try {
                            if (isUnread) {
                              await api.post(`/client-portal/concierge/conversations/${activeConvId}/read-all`);
                            } else {
                              await api.post(`/client-portal/concierge/conversations/${activeConvId}/mark-unread`);
                            }
                            fireChanged();
                            fetchConversations();
                            fetchActive();
                          } catch (e) {
                            // eslint-disable-next-line no-alert
                            alert(e?.response?.data?.detail || "Couldn't update read state");
                          }
                        }}
                        title={activeConv.client_unread_since_message_id
                          ? "Mark this thread as read (just for me)"
                          : "Mark this thread as unread (just for me)"}
                        className="shrink-0 size-9 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background))] transition-colors"
                        data-testid="thread-toggle-unread"
                        aria-label={activeConv.client_unread_since_message_id ? "Mark as read" : "Mark as unread"}
                      >
                        {activeConv.client_unread_since_message_id
                          ? <MailOpen className="size-4" strokeWidth={1.5} />
                          : <Mail className="size-4" strokeWidth={1.5} />}
                      </button>
                    </div>
                  </div>

                  {/* Letters */}
                  <div
                    className="flex-1 overflow-y-auto px-5 sm:px-8 py-4"
                    data-testid="thread-messages"
                  >
                    {messages.length === 0 ? (
                      <div className="h-full flex items-center justify-center min-h-[40vh]">
                        <p className="font-serif italic text-[15px] text-[hsl(var(--foreground-muted))]">
                          A quiet beginning — say hello.
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[hsl(var(--border-subtle))]">
                        {grouped.map((g) =>
                          g.kind === "day" ? (
                            <DayRule key={g.key} label={g.label} />
                          ) : (
                            <MessageLetter
                              key={g.key}
                              msg={g.msg}
                              prev={g.prev}
                              convId={activeConvId}
                              onChanged={() => { fetchActive(); fetchConversations(); }}
                            />
                          ),
                        )}
                        {activeConv.advisor_typing ? <TypingIndicator /> : null}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  <Composer onSend={handleSend} busy={sendingBusy} onTyping={sendTypingPing} />
                </Surface>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
